/**
 * Mailchimp Calendar Email Service
 * Handles appointment confirmation and reminder emails via Edge Function
 * NOTE: All email sending is done server-side to protect API keys
 */

import { supabase } from "@/integrations/supabase/client";

interface AppointmentEmailData {
  customerEmail: string;
  customerName: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentId: string;
}

/**
 * Format date to readable format WITHOUT timezone conversion (CRITICAL FIX)
 * This prevents the 1-day-early bug by parsing date components manually
 * Example: "2024-11-10" -> "Monday, November 10, 2024"
 */
export function formatAppointmentDate(dateString: string): string {
  // Parse YYYY-MM-DD without timezone conversion
  const [year, month, day] = dateString.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day, 12, 0, 0); // Use noon to avoid DST issues
  
  return dateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format time to 12-hour format with timezone (e.g., "2:00 PM CST")
 * CRITICAL: Always uses 12-hour format for consistency
 */
export function formatAppointmentTime(timeString: string, dateString: string): string {
  // Parse date manually to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const [hours, minutes] = timeString.split(':').map(Number);
  const datetime = new Date(year, month - 1, day, hours, minutes);
  
  return datetime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  });
}

/**
 * Send appointment confirmation email via Edge Function
 */
export async function sendConfirmationEmail(data: AppointmentEmailData): Promise<void> {
  console.log('📧 Sending confirmation email to:', data.customerEmail);

  const { data: result, error } = await supabase.functions.invoke('send-calendar-email', {
    body: {
      type: 'confirmation',
      customerEmail: data.customerEmail,
      customerName: data.customerName,
      appointmentDate: formatAppointmentDate(data.appointmentDate),
      appointmentTime: formatAppointmentTime(data.appointmentTime, data.appointmentDate),
      appointmentId: data.appointmentId
    }
  });

  if (error) {
    console.error('❌ Failed to send confirmation email:', error);
    throw new Error(`Failed to send confirmation email: ${error.message}`);
  }

  if (!result?.success) {
    console.error('❌ Email service error:', result?.error);
    throw new Error(result?.error || 'Failed to send confirmation email');
  }

  console.log('✅ Confirmation email sent successfully');
}

/**
 * Send 24-hour reminder email via Edge Function
 */
export async function send24HourReminder(data: AppointmentEmailData): Promise<void> {
  console.log('📧 Sending 24-hour reminder to:', data.customerEmail);

  const { data: result, error } = await supabase.functions.invoke('send-calendar-email', {
    body: {
      type: '24hr-reminder',
      customerEmail: data.customerEmail,
      customerName: data.customerName,
      appointmentDate: formatAppointmentDate(data.appointmentDate),
      appointmentTime: formatAppointmentTime(data.appointmentTime, data.appointmentDate),
      appointmentId: data.appointmentId
    }
  });

  if (error) {
    console.error('❌ Failed to send 24-hour reminder:', error);
    throw new Error(`Failed to send 24-hour reminder: ${error.message}`);
  }

  if (!result?.success) {
    console.error('❌ Email service error:', result?.error);
    throw new Error(result?.error || 'Failed to send 24-hour reminder');
  }

  console.log('✅ 24-hour reminder sent successfully');
}

/**
 * Send 1-hour reminder email via Edge Function
 */
export async function send1HourReminder(data: AppointmentEmailData): Promise<void> {
  console.log('📧 Sending 1-hour reminder to:', data.customerEmail);

  const { data: result, error } = await supabase.functions.invoke('send-calendar-email', {
    body: {
      type: '1hr-reminder',
      customerEmail: data.customerEmail,
      customerName: data.customerName,
      appointmentTime: formatAppointmentTime(data.appointmentTime, data.appointmentDate),
      appointmentId: data.appointmentId
    }
  });

  if (error) {
    console.error('❌ Failed to send 1-hour reminder:', error);
    throw new Error(`Failed to send 1-hour reminder: ${error.message}`);
  }

  if (!result?.success) {
    console.error('❌ Email service error:', result?.error);
    throw new Error(result?.error || 'Failed to send 1-hour reminder');
  }

  console.log('✅ 1-hour reminder sent successfully');
}
